import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, Check, Download, ExternalLink, FileText } from "lucide-react";
import type React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Separator } from "../../../components/ui/separator";

type ViewPdfState = {
	pdfUrl?: string;
	pdfTitle?: string;
};

function Heading({ children, className = "" }: { children: React.ReactNode; className?: string }) {
	return (
		<h1 className={`text-2xl font-semibold leading-tight tracking-tight text-gray-900 ${className}`}>{children}</h1>
	);
}

function Header({ children }: { children?: React.ReactNode }) {
	return (
		<header className="sticky top-0 z-20 border-b border-gray-200/80 bg-white/90 backdrop-blur-sm">
			<div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-8">
				<Link to="/" className="flex items-center" aria-label="Dproz home">
					<img className="h-8 w-auto" src="/images/logo.png" alt="Dproz" />
				</Link>
				{children}
			</div>
		</header>
	);
}

function EmptyState() {
	return (
		<div className="min-h-[100dvh] bg-[#f4f6f9] font-inter">
			<Header />
			<main className="mx-auto flex max-w-[1400px] justify-center px-4 pt-20">
				<Card className="max-w-md p-8 text-center">
					<div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
						<FileText className="h-7 w-7" strokeWidth={1.75} />
					</div>
					<Heading className="mt-6">No resume to preview</Heading>
					<p className="mt-2 text-sm leading-relaxed text-gray-500">
						We couldn&apos;t find a resume to show here. Head back to your resumes to open or download one.
					</p>
					<Button asChild className="mt-6">
						<Link to="/my-resumes">
							<ArrowLeft className="mr-2 h-4 w-4" />
							Go to My Resumes
						</Link>
					</Button>
				</Card>
			</main>
		</div>
	);
}

function ViewPdf() {
	const navigate = useNavigate();
	const reduceMotion = useReducedMotion();
	const state = (useLocation().state as ViewPdfState | null) ?? {};
	const { pdfUrl, pdfTitle } = state;

	if (!pdfUrl) return <EmptyState />;

	const downloadName = pdfTitle ? `${pdfTitle}.pdf` : "resume.pdf";

	const rise = (delay: number) => ({
		initial: reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 },
		animate: { opacity: 1, y: 0 },
		transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const, delay },
	});

	return (
		<div className="min-h-[100dvh] bg-[#f4f6f9] font-inter">
			<Header>
				<Button variant="outlineSecondary" size="sm" onClick={() => navigate("/my-resumes")}>
					Done
				</Button>
			</Header>

			<main className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
				<div className="flex flex-col items-start gap-6 lg:flex-row lg:gap-8">
					{/* PDF sheet */}
					<motion.section {...rise(0)} className="order-2 w-full min-w-0 lg:order-1 lg:flex-1">
						<Card className="overflow-hidden border-black/5 p-0 shadow-[0_1px_3px_rgba(16,24,40,.06),0_18px_40px_-16px_rgba(16,24,40,.20)]">
							<iframe
								src={pdfUrl}
								title={pdfTitle || "Resume preview"}
								className="h-[calc(100dvh-8rem)] min-h-[560px] w-full"
							/>
						</Card>
					</motion.section>

					{/* Completion + actions */}
					<motion.aside
						{...rise(0.08)}
						className="order-1 w-full self-stretch lg:order-2 lg:sticky lg:top-24 lg:w-[20rem] lg:self-start"
					>
						<Card className="p-6">
							<Badge className="gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-primary-700">
								<Check className="h-3.5 w-3.5" strokeWidth={2.5} />
								Ready
							</Badge>
							<Heading className="mt-3">Your resume is ready</Heading>
							<p className="mt-2 text-sm leading-relaxed text-gray-500">
								{pdfTitle ? `“${pdfTitle}” is` : "Your resume is"} generated and ready to go. Download the PDF, or open
								it in a new tab to save it your way.
							</p>

							<div className="mt-5 flex flex-col gap-2">
								<Button asChild className="w-full">
									<a href={pdfUrl} download={downloadName}>
										<Download className="mr-2 h-4 w-4" />
										Download PDF
									</a>
								</Button>
								<Button asChild variant="ghost" className="w-full">
									<a href={pdfUrl} target="_blank" rel="noopener noreferrer">
										<ExternalLink className="mr-2 h-4 w-4" />
										Open in new tab
									</a>
								</Button>
							</div>

							<Separator className="my-5" />
							<p className="text-xs leading-relaxed text-gray-400">
								If the download doesn&apos;t start, use your browser&apos;s save option from the preview toolbar above
								the resume.
							</p>
						</Card>
					</motion.aside>
				</div>
			</main>
		</div>
	);
}

export default ViewPdf;
